<template>
  <ModalBase
    v-if="!currentEnv.isDemoEnv"
    :titel="titel"
    :show="show"
    :button-confirm-disabled="message.length === 0 || (phone.length === 0 && requestCallBack) || contactReason.length === 0"
    button-confirm-text="Senden"
    button-cancel-text="Schließen"
    class="w-full"
    @on-cancel="emit('onCancel')"
    @on-confirm="sendForm"
  >
    <template #icon>
      <BIconChatLeft />
    </template>

    <template #content>
      <div class="mx-auto max-w-3xl">
        <form
          ref="contact-form"
          class="grid grid-cols-2 gap-y-6 gap-x-4"
        >
          <SelectMenu v-model:selected="contactReason" label="Grund*" :options="contactReasonOptions" />

          <SelectMenu v-model:selected="priority" label="Priorität" :options="priorityOptions" />

          <div class="col-span-2 flex">
            <input
              v-model="requestCallBack"
              class="checkbox-standard mt-1"
              name="callback"
              type="checkbox"
              :value="requestCallBack"
            >
            <label class="block text-sm font-medium text-secondary-lighter cursor-pointer" @click="requestCallBack = !requestCallBack">
              Ich möchte zurückgerufen werden</label>
          </div>

          <div v-if="requestCallBack" class="col-span-2">
            <input
              v-model="phone"
              type="text"
              name="phone"
              autocomplete="off"
              class="text-input-standard"
              placeholder="Telefon*"
            >
          </div>
          <div class="col-span-2">
            <textarea
              v-model="message"
              name="message"
              rows="4"
              required
              class="text-input-standard"
              placeholder="Nachricht*"
            />
          </div>
          <p
            :class="[status.startsWith('Ticket wurde erfolgreich erstellt') ? 'text-special-green-text' : 'text-red', 'col-span-2 text-sm']"
          >
            {{ status }}
          </p>
        </form>
      </div>
    </template>
  </ModalBase>
</template>
<script setup lang='ts'>
import { BIconChatLeft } from 'bootstrap-icons-vue'
import ModalBase from '~/components/shared/modals/ModalBase.vue'
import SelectMenu from '~/components/shared/basics/SelectMenu.vue'
const { $toast, $logger, $api } = useNuxtApp()
const emit = defineEmits(['onOk', 'onCancel'])
const { currentUserSession, userOrganization, currentEnv } = useCurrentUserSession()
const { serviceprovider } = useCurrentServiceprovider()
const { currentClient } = useCurrentClient()

// the token only carries the org UUID, resolve its name from the already loaded serviceprovider/client
const company = computed((): string => {
  let name = ''
  if (serviceprovider.value?.uuid === userOrganization) {
    name = serviceprovider.value.name
  } else if (currentClient.value?.client_id_pk === userOrganization) {
    name = currentClient.value.client_name
  }
  return name ? `${name} (${userOrganization})` : userOrganization
})

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  titel: {
    type: String,
    required: true
  },
  initialMessage: {
    type: String,
    default: ''
  },
  initialContactReason: {
    type: String,
    default: ''
  }
})

const phone = ref('')
const message = ref(props.initialMessage)
const status = ref('')
const contactReason = ref(props.initialContactReason)
const priority = ref('Niedrig')

// Watch for prop changes to update initial values when modal is opened
watch(() => props.show, (newVal) => {
  if (newVal && props.initialMessage) {
    message.value = props.initialMessage
  }
  if (newVal && props.initialContactReason) {
    contactReason.value = props.initialContactReason
  }
})
const requestCallBack = ref(false)

const contactReasonOptions = [
  'Technischer Support', 'Feature Request', 'Vertragliches', 'Feedback', 'Anderes'
]
const priorityOptions = [
  'Niedrig', 'Mittel', 'Hoch', 'Sehr hoch'
]

function resetValues (): void {
  phone.value = ''
  message.value = ''
  requestCallBack.value = false
  contactReason.value = ''
  priority.value = ''
}

function areRequireValuesFilled (): boolean {
  if (message.value.length === 0 || (phone.value.length === 0 && requestCallBack.value) || contactReason.value.length === 0) {
    status.value = 'Bitte alle Pflichtfelder ausfüllen'
    return false
  }

  status.value = ''
  return true
}

function getContactReasonInternal (value): string {
  switch (value) {
    case 'Technischer Support':
      return 'PRODUCT_ISSUE'
    case 'Feature Request':
      return 'FEATURE_REQUEST'
    case 'Vertragliches':
      return 'BILLING_ISSUE'
    case 'Feedback':
      return 'FEEDBACK'
    case 'Anderes':
      return 'GENERAL_INQUIRY'
  }
}

async function sendForm (): Promise<void> {
  if (currentEnv.isDemoEnv) {
    emit('onCancel')
    $toast.error('Demo Umgebung', 'Nachrichten können in der Demo Umgebung nicht gesendet werden')
    return
  }

  const preCheckSucceed = areRequireValuesFilled()

  if (preCheckSucceed) {
    try {
      const data = {
        firstname: currentUserSession?.value?.user.givenName,
        lastname: currentUserSession?.value?.user.familyName,
        company: company.value,
        email: currentUserSession?.value?.user.email,
        phone: phone.value,
        message: message.value,
        contactReason: getContactReasonInternal(contactReason.value),
        requestCallBack: requestCallBack.value,
        priority: priority.value
      }

      const res = await $api.other.createSupportRequest(data)

      if (res.status === 'success') {
        status.value = 'Ticket wurde erfolgreich erstellt! Wir melden uns so schnell wie möglich'
        resetValues()
      } else {
        status.value =
          'Es gab ein Problem beim Erstellen des Tickets. Bitte versuchen Sie es erneut oder schreiben Sie an support@sec-auditor.com'
        $logger.error(`Error: ${res.message}`)
      }
    } catch (e) {
      status.value =
        'Es gab ein Problem beim Erstellen des Tickets. Bitte versuchen Sie es erneut oder schreiben Sie an support@sec-auditor.com'
      $logger.error(e)
    }
  }
}

</script>
